import { useEffect, useMemo, useState, type ComponentType, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
import { ArrowLeft, Check, ChevronDown, Chrome, Compass, Copy, Download, ExternalLink, FileBox, Flame, ImageDown, MousePointerClick, ShieldCheck, Sparkles } from 'lucide-react';
import { PublicShell } from '../components/PublicShell';
import { usePageMeta } from '../hooks/usePageMeta';
import { publicUrl, hreflangAlternates, activeLang, ogLocaleOf } from '../lib/publicSeo';
import { api } from '../lib/api';
import { trackEvent } from '../lib/analytics';
import { cn } from '../lib/utils';
import { detectBrowser, installTargetFor } from '../lib/detectBrowser';
import type { ExtensionBrowser, ExtensionRelease, LatestExtensionReleases } from '../types';

function formatSize(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  const kb = bytes / 1024;
  return `${kb.toFixed(0)} KB`;
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

const FEATURE_KEYS = ['quickSave', 'domMeta', 'screenshot', 'secure'] as const;
const FEATURE_ICONS: Record<(typeof FEATURE_KEYS)[number], ComponentType<{ className?: string }>> = {
  quickSave: MousePointerClick,
  domMeta: FileBox,
  screenshot: ImageDown,
  secure: ShieldCheck,
};

// AMO public listing for the published stable Firefox build. Locale-aware so
// UA users land on the localized AMO page (en-US / uk) which Mozilla resolves
// to the canonical add-on regardless of locale slug.
function amoUrlForLocale(lang: string): string {
  const locale = lang.toLowerCase().startsWith('uk') ? 'uk' : 'en-US';
  return `https://addons.mozilla.org/${locale}/firefox/addon/markflow-fyi/`;
}

const TILE_CARD = 'rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900';

interface CompactCta {
  href: string;
  label: string;
  external?: boolean;
  download?: string;
}

interface TileDescriptor {
  key: ExtensionBrowser;
  Icon: ComponentType<{ className?: string }>;
  name: string;
  headingBadge?: ReactNode;
  // Small install link shown in the collapsed compact row; null while the
  // build is unavailable (loading / coming soon) so only the expander shows.
  compactCta: CompactCta | null;
  body: ReactNode;
}

// Full-size install tile. `emphasized` adds the violet ring + "Recommended for
// your browser" badge for the visitor's detected browser.
function ExpandedTile({ tile, emphasized }: { tile: TileDescriptor; emphasized: boolean }) {
  const { t } = useTranslation();
  const { Icon, name, headingBadge, body } = tile;
  return (
    <section
      className={cn(
        TILE_CARD,
        'p-5 sm:p-6 space-y-4',
        emphasized && 'ring-2 ring-violet-500/60 border-violet-300 dark:border-violet-600 shadow-md',
      )}
    >
      <div className="space-y-2">
        {emphasized && (
          <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 dark:bg-violet-900/30 px-2.5 py-1 text-xs font-medium text-violet-700 dark:text-violet-300">
            <Sparkles className="w-3.5 h-3.5" />
            {t('download.recommendedBadge')}
          </span>
        )}
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-zinc-700 dark:text-zinc-300" />
          <h2 className="text-lg font-semibold">{name}</h2>
          {headingBadge}
        </div>
      </div>
      {body}
    </section>
  );
}

// De-emphasized install tile: a single collapsed row (logo + name + small
// install link) that expands in place to the full body. The chevron is the
// only motion, gated behind motion-safe for prefers-reduced-motion users.
function CompactTile({ tile }: { tile: TileDescriptor }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { Icon, name, headingBadge, compactCta, body } = tile;
  return (
    <section className={cn(TILE_CARD, 'px-4 py-3')}>
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5 text-zinc-500 dark:text-zinc-400 shrink-0" />
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-50 truncate">{name}</span>
          {headingBadge}
        </div>
        <div className="ml-auto flex items-center gap-3 shrink-0">
          {compactCta && (
            <a
              href={compactCta.href}
              onClick={() => trackEvent('download_click', { browser: tile.key, channel: 'compact' })}
              {...(compactCta.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              {...(compactCta.download ? { download: compactCta.download } : {})}
              className="text-sm font-medium text-violet-600 dark:text-violet-400 hover:underline"
            >
              {compactCta.label}
            </a>
          )}
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-label={open ? t('download.collapse') : t('download.expand')}
            className="inline-flex items-center justify-center rounded p-1 text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100"
          >
            <ChevronDown className={cn('w-4 h-4 motion-safe:transition-transform', open && 'rotate-180')} />
          </button>
        </div>
      </div>
      {open && <div className="border-t border-zinc-200 dark:border-zinc-800 mt-3 pt-4">{body}</div>}
    </section>
  );
}

const ROUTE = '/download';
const OG_IMAGE = 'https://markflow.fyi/og.png';

export function DownloadPage() {
  const { t, i18n } = useTranslation();
  const lang = activeLang(i18n.resolvedLanguage);
  const pageUrl = publicUrl(ROUTE, lang);

  usePageMeta({
    title: t('download.meta.title'),
    description: t('download.meta.description'),
    canonical: pageUrl,
    ogTitle: t('download.meta.ogTitle'),
    ogDescription: t('download.meta.ogDescription'),
    ogImage: OG_IMAGE,
    ogUrl: pageUrl,
    htmlLang: lang,
    ogLocale: ogLocaleOf(lang),
    ogLocaleAlternate: ogLocaleOf(lang === 'uk' ? 'en' : 'uk'),
    alternates: hreflangAlternates(ROUTE),
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: t('download.meta.title'),
      description: t('download.meta.description'),
      url: pageUrl,
    },
  });
  const [releases, setReleases] = useState<LatestExtensionReleases | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [copiedBrowser, setCopiedBrowser] = useState<ExtensionBrowser | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.extensionReleases
      .latest()
      .then((r) => {
        if (!cancelled) setReleases(r);
      })
      .catch((err) => {
        console.error('[download] load failed', err);
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const chrome = releases?.chrome?.stable ?? null;
  const opera = releases?.opera?.stable ?? null;
  const firefoxNightly = releases?.firefox?.nightly ?? null;
  // Detected once on mount; drives which install tile is featured.
  const target = useMemo(() => installTargetFor(detectBrowser()), []);
  const amoUrl = amoUrlForLocale(i18n.language);

  const onCopyChecksum = async (release: ExtensionRelease) => {
    try {
      await navigator.clipboard.writeText(release.sha256);
      setCopiedBrowser(release.browser);
      setTimeout(() => setCopiedBrowser(null), 1500);
    } catch (err) {
      console.error('[download] copy failed', err);
    }
  };

  // Shared renderer for the per-browser release card body. Both Chrome and
  // Firefox share the same shape (download CTA, version line, optional notes,
  // checksum, install steps); the i18n namespace switches per browser so the
  // steps copy stays browser-specific. Keep this inlined — extracting a
  // standalone component would just pass the same useTranslation hooks back.
  const renderReleaseBody = (browser: ExtensionBrowser, release: ExtensionRelease) => {
    const ns = `download.${browser}`;
    const copied = copiedBrowser === browser;
    return (
      <>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <a
            href={release.zipUrl}
            download={`markflow-${browser}-${release.version}.zip`}
            onClick={() => trackEvent('download_click', { browser, channel: 'zip' })}
            className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-violet-600 to-fuchsia-500 hover:from-violet-700 hover:to-fuchsia-600 transition-colors shadow-sm"
          >
            <Download className="w-4 h-4" />
            {t(`${ns}.cta`)}
          </a>
          <span className="text-xs text-zinc-500">
            {t(`${ns}.versionLine`, {
              version: release.version,
              size: formatSize(release.sizeBytes),
              date: formatDate(release.releasedAt, i18n.language),
            })}
          </span>
        </div>

        {release.notes && (
          <p className="text-xs text-zinc-600 dark:text-zinc-400 whitespace-pre-line">{release.notes}</p>
        )}

        <div className="flex items-center gap-2 text-xs">
          <span className="font-mono text-zinc-500">{t(`${ns}.checksumLabel`)}</span>
          <code className="font-mono text-zinc-700 dark:text-zinc-300 truncate">{release.sha256}</code>
          <button
            type="button"
            onClick={() => onCopyChecksum(release)}
            className="inline-flex items-center gap-1 text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors shrink-0"
            title={t(`${ns}.copyChecksum`)}
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            <span>{copied ? t(`${ns}.copiedChecksum`) : t(`${ns}.copyChecksum`)}</span>
          </button>
        </div>

        <div className="border-t border-zinc-200 dark:border-zinc-800 pt-4 space-y-2">
          <h3 className="text-sm font-semibold">{t(`${ns}.stepsHeading`)}</h3>
          <ol className="list-decimal list-inside space-y-1 text-sm text-zinc-600 dark:text-zinc-400">
            <li>{t(`${ns}.steps.1`)}</li>
            <li>{t(`${ns}.steps.2`)}</li>
            <li>{t(`${ns}.steps.3`)}</li>
          </ol>
          <p className="text-xs text-zinc-500 pt-1">{t(`${ns}.afterInstall`)}</p>
        </div>
      </>
    );
  };

  const chromeBody = (
    <div className="space-y-4">
      {chrome ? (
        renderReleaseBody('chrome', chrome)
      ) : (
        <p className="text-sm text-zinc-500">
          {loadFailed ? t('download.loadFailed') : releases ? t('download.empty') : t('download.loading')}
        </p>
      )}
    </div>
  );

  const firefoxBody = (
    <div className="space-y-5">
      {/* Primary: AMO. Always shown; the stable Firefox build no longer ships
          as self-hosted .xpi — Mozilla signs + distributes it. */}
      <div className="space-y-2">
        <a
          href={amoUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackEvent('download_click', { browser: 'firefox', channel: 'amo' })}
          className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-violet-600 to-fuchsia-500 hover:from-violet-700 hover:to-fuchsia-600 transition-colors shadow-sm"
        >
          <ExternalLink className="w-4 h-4" />
          {t('download.firefox.amoCta')}
        </a>
        <p className="text-xs text-zinc-500 leading-relaxed">{t('download.firefox.amoBody')}</p>
      </div>

      {/* Secondary: self-hosted nightly. Hidden if no nightly release is
          published — keeps the tile uncluttered for normal users. */}
      {firefoxNightly && (
        <div className="border-t border-zinc-200 dark:border-zinc-800 pt-4 space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">{t('download.firefox.nightlyHeading')}</h3>
            <span className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300 uppercase tracking-wide">
              {t('download.firefox.nightlyBadge')}
            </span>
          </div>
          <p className="text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed">{t('download.firefox.nightlyBody')}</p>
          {renderReleaseBody('firefox', firefoxNightly)}
        </div>
      )}
    </div>
  );

  const operaBody = opera ? (
    <div className="space-y-4">{renderReleaseBody('opera', opera)}</div>
  ) : (
    <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">{t('download.opera.comingSoonBody')}</p>
  );

  const compactInstall = t('download.compactInstall');
  const tiles: Record<ExtensionBrowser, TileDescriptor> = {
    chrome: {
      key: 'chrome',
      Icon: Chrome,
      name: t('download.chrome.heading'),
      compactCta: chrome
        ? { href: chrome.zipUrl, download: `markflow-chrome-${chrome.version}.zip`, label: compactInstall }
        : null,
      body: chromeBody,
    },
    firefox: {
      key: 'firefox',
      Icon: Flame,
      name: t('download.firefox.heading'),
      compactCta: { href: amoUrl, external: true, label: compactInstall },
      body: firefoxBody,
    },
    opera: {
      key: 'opera',
      Icon: Compass,
      name: t('download.opera.heading'),
      headingBadge: opera ? undefined : (
        <span className="inline-flex items-center rounded-full bg-zinc-100 dark:bg-zinc-800 px-2 py-0.5 text-[10px] font-medium text-zinc-600 dark:text-zinc-300 uppercase tracking-wide">
          {t('download.opera.comingSoon')}
        </span>
      ),
      compactCta: opera
        ? { href: opera.zipUrl, download: `markflow-opera-${opera.version}.zip`, label: compactInstall }
        : null,
      body: operaBody,
    },
  };

  // Fixed install order; the detected browser (if any) is hoisted to the front
  // and featured, the rest collapse to compact rows. No detection / Safari /
  // unknown → neutral, all-equal full tiles (also the SSR fallback).
  const order: ExtensionBrowser[] = ['chrome', 'firefox', 'opera'];
  const otherKeys = target ? order.filter((key) => key !== target) : [];

  return (
    <PublicShell>
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-10">
          <Link
            href="/"
            className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('footer.backToApp')}
          </Link>

          <section className="space-y-3">
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50">
              {t('download.title')}
            </h1>
            <p className="text-zinc-600 dark:text-zinc-400 leading-relaxed">{t('download.subtitle')}</p>
            <p className="text-xs text-zinc-500">{t('download.webStoreNotice')}</p>
          </section>

          <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {FEATURE_KEYS.map((key) => {
              const Icon = FEATURE_ICONS[key];
              return (
                <div
                  key={key}
                  className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4 space-y-2"
                >
                  <div className="flex items-center gap-2 text-violet-600 dark:text-violet-400">
                    <Icon className="w-4 h-4" />
                    <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">
                      {t(`download.features.${key}.title`)}
                    </h3>
                  </div>
                  <p className="text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed">
                    {t(`download.features.${key}.body`)}
                  </p>
                </div>
              );
            })}
          </section>

          {target ? (
            <div className="space-y-4">
              <ExpandedTile tile={tiles[target]} emphasized />
              <div className="space-y-2">
                <p className="text-xs font-medium uppercase tracking-wide text-zinc-400 dark:text-zinc-500 pt-2">
                  {t('download.otherBrowsers')}
                </p>
                {otherKeys.map((key) => (
                  <CompactTile key={key} tile={tiles[key]} />
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {order.map((key) => (
                <ExpandedTile key={key} tile={tiles[key]} emphasized={false} />
              ))}
            </div>
          )}
        </div>
      </main>
    </PublicShell>
  );
}
